// WHITELABEL-BRAND-LANG-2 — the language of a LOGGED-OUT visitor on a
// branded host, and the one place that settles the brand record for boot.
//
// bootShell resolves the interface language at step 7b, AFTER the auth gate,
// because the strongest signals (the account preference, the workspace
// default) need a session. The branded LOGIN never reaches 7b: the gate
// bounces the anonymous visitor to /login and returns before it. So the auth
// shell mounted with first paint's `localStorage.lang || en`, and the org's
// `branding.lang` — the only signal that exists BEFORE sign-in — reached the
// marks and the tagline but never the copy. Measured twice on
// gita.pr.symbols.app (2026-09-08): scope `lang: 'ka'`, hydrated
// `<html lang="en">`, English login, no stored preference.
import { DEFAULT_LANG, langDir } from './languages.js'
import { hasI18nBundle, loadI18nNamespaces, mergeAllNamespaces } from './i18nLoader.js'

/**
 * The brand record, settled. The entry seeds `state.whitelabelBrand` with a
 * 600ms race (a slow lookup must never delay the mount) and hands boot the
 * SAME single request as `state.whitelabelBrandLookup`. When the seed won,
 * this is a plain read; when it lost, the lookup is awaited here, bounded —
 * an unreachable API cannot hold the shell, a slow one cannot paint English
 * on a `ka` host. Platform hosts have neither and get null at once.
 */
export const settleWhitelabelBrand = async (state, timeoutMs = 2000) => {
  if (!state) return null
  if (state.whitelabelBrand) return state.whitelabelBrand
  if (!state.whitelabelBrandLookup) return null
  const TIMED_OUT = Symbol('whitelabelBrandLookupTimeout')
  const settled = await Promise.race([
    Promise.resolve(state.whitelabelBrandLookup).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), timeoutMs))
  ])
  if (!settled || settled === TIMED_OUT) return null
  if (!state.whitelabelBrand) state.whitelabelBrand = settled
  return settled
}

/**
 * Resolve, load and stamp the language for a SIGNED-OUT page — the /login
 * bounce and every guest route the gate lets through. KA-1 (2026-10-25): a
 * Georgian visitor on the platform's own /login got English — the only signal
 * read was a white-label brand, which the platform host has not. The order:
 *   1. `?lang=` on the page, or inside its `next` (the bounce keeps the
 *      inbound destination there, so `/?lang=ka` reaches /login as
 *      `?next=%2F%3Flang%3Dka`) — an explicit link;
 *   2. the brand's `lang` on a white-label host — the org chose it;
 *   3. the browser's languages (`navigator.languages`) in the visitor's order,
 *      the FIRST shipped one (`['fr', 'ka']` reads Georgian, `['en-US', 'ka']`
 *      English);
 *   4. English.
 * Region subtags drop (`ka-GE` → `ka`); an unshipped code at any level is
 * skipped. The order is written HERE, self-contained (the frank FA204 rule
 * languages.js names: a `functions/` export cannot read a sibling's helper). This device's `localStorage.lang` is not read (Nika 2026-09-10: a
 * stored choice must not paint a host that never chose it). Loads the PUBLIC
 * namespaces (`common`, `auth`, plus any the caller names) into the polyglot
 * bootShell minted at step 1b — the same merge 7b performs for a signed-in
 * boot — then writes `state.lang` and `<html lang/dir>`. Persists nothing:
 * a resolved language is not a choice (see 7b's rationale).
 *
 * `query` and `browserLanguages` default to the live page and browser; tests
 * pass them.
 *
 * @returns {Promise<string>} the language applied
 */
export const applyPublicBootLang = async ({
  context,
  namespaces,
  query,
  browserLanguages
} = {}) => {
  const state = context && context.state
  const brand = await settleWhitelabelBrand(state)
  const search =
    typeof query === 'string'
      ? query
      : typeof window !== 'undefined' && window.location
        ? window.location.search
        : ''
  const nav = typeof navigator !== 'undefined' ? navigator : null
  const browser = Array.isArray(browserLanguages)
    ? browserLanguages
    : nav && Array.isArray(nav.languages) && nav.languages.length
      ? nav.languages
      : nav && typeof nav.language === 'string'
        ? [nav.language]
        : []
  const SUPPORTED = ['en', 'ka']
  const primary = (c) => (typeof c === 'string' ? c.trim().toLowerCase().split(/[-_]/)[0] : '')
  const ok = (c) => SUPPORTED.indexOf(c) !== -1
  const fromParams = (params) => {
    const code = primary(params.get('lang'))
    return ok(code) ? code : null
  }
  let fromQuery = null
  try {
    const params = new URLSearchParams(search || '')
    fromQuery = fromParams(params)
    const next = params.get('next')
    const q = !fromQuery && typeof next === 'string' ? next.indexOf('?') : -1
    if (q !== -1) fromQuery = fromParams(new URLSearchParams(next.slice(q).split('#')[0]))
  } catch (_) {
    fromQuery = null
  }
  const fromBrand = primary(brand && brand.lang)
  const lang =
    fromQuery || (ok(fromBrand) ? fromBrand : null) || browser.map(primary).find(ok) || 'en'
  const poly = context && context.polyglot
  if (poly && poly.translations) {
    const ns = Array.from(new Set(['common', 'auth', ...(namespaces || [])]))
    await loadI18nNamespaces(DEFAULT_LANG, ns)
    if (!Object.keys(poly.translations[DEFAULT_LANG] || {}).length) {
      poly.translations[DEFAULT_LANG] = mergeAllNamespaces(DEFAULT_LANG)
    }
    if (lang !== DEFAULT_LANG) {
      const merged = await loadI18nNamespaces(lang, ns)
      if (Object.keys(merged).length) {
        const existing = poly.translations[lang] || {}
        poly.translations[lang] = ns.some((n) => hasI18nBundle(lang, n))
          ? { ...existing, ...merged }
          : { ...merged, ...existing }
      }
    }
  }
  if (state) state.lang = lang
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lang
    document.documentElement.dir = langDir(lang)
  }
  return lang
}
